import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Button, Icon, Tooltip, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import styles from 'modules/Forms/Account/Create/account-form-creation.module.scss'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import phoneValidator from 'utils/validators/phoneValidator'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import phoneMask from 'utils/constants/phoneMask'
import i18next from 'i18next'

export const WowBloggerSchema = () =>
  Yup.object().shape({
    network: Yup.string().required('common::validation_messages.required'),
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    email: Yup.string()
      .test(
        'emailValidator',
        'common::validation_messages.incorrect_email_format',
        emailValidator,
      )
      .required('common::validation_messages.required'),
    last_name: Yup.string()
      .max(
        255,
        i18next.t('common::validation_messages.incorrect_text_length_max', {
          limit: 255,
        }),
      )
      .required('common::validation_messages.required'),
    first_name: Yup.string()
      .max(
        255,
        i18next.t('common::validation_messages.incorrect_text_length_max', {
          limit: 255,
        }),
      )
      .required('common::validation_messages.required'),
    phone: Yup.string()
      .nullable()
      .test(
        'phoneValidator',
        'common::validation_messages.incorrect_phone_format',
        phoneValidator,
      ),
  })

let CreateWowBloggerAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(
    form,
    onClose,
    multiLogins,
  )
  const { email, user } = useSelector((state) => ({
    email: selector(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  return (
    <>
      <Typography variant={'Header-60'} className={'margin-bottom-16'}>
        {t('services::networks.' + networkType)}
      </Typography>
      <Typography
        variant={'Body-20'}
        color="text-secondary"
        className={'margin-bottom-16'}
      >
        {t('services::forms.register_account.form_subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(submitAccountData)} noValidate>
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={
              'services::forms.register_account.wowblogger.fields.login.label'
            }
          />
        </div>

        <div
          className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}
        >
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.wowblogger.fields.email.label',
            )}
            t={t}
          />

          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.wowblogger.fields.email.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="first_name"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.wowblogger.fields.first_name.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="last_name"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.wowblogger.fields.last_name.label',
            )}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            fullWidth
            normalize={phoneFieldNormalize}
            name="phone"
            component={renderMaskedInput}
            placeholderChar={'\u2002'}
            placeholder={'+ ( )'}
            mask={phoneMask}
            label={t(
              'services::forms.register_account.zeropark.fields.phone.label',
            )}
            t={t}
          />
        </div>

        <div
          className={clsx(
            formStyles['formControl__action-buttons'],
            styles['action-buttons'],
          )}
        >
          <Button
            disabled={submitting}
            onClick={onClose}
            variant={'ghost'}
            grey
            size="l"
            type="button"
          >
            {t('common::form_buttons.cancel')}
          </Button>
          <Button
            disabled={submitting}
            size="l"
            variant="primary"
            type="submit"
            bgColor={'Label-GU-Primary'}
          >
            {t('services::forms.register_account.submit_button')}
          </Button>
        </div>
      </form>
    </>
  )
}

CreateWowBloggerAccountForm = reduxForm({
  form: 'CreateWowBloggerAccountForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    last_name: '',
    first_name: '',
    phone: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateWowBloggerAccountForm)

CreateWowBloggerAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateWowBloggerAccountForm
